import { MutableRefObject } from "react";
import { NavigateFunction } from "react-router-dom";
import { LatestMedicalPaFormData } from "../../../../api/getCalls/getLatestMedicalPaFormData";
import { DumpLatestMedicalPaFormDataRequest } from "../../../../api/postCall/dumpLatestMedicalPaFormData";
import { User } from "../../../../data-model/firebaseAuthModel";
import { HpcsCode } from "../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { createOrderDataFromContextFormData } from "./createOrderDataFromContextFormData";
import { handleFaxSubmission } from "./handleFaxSubmission";
import { handleSubmissionForNchPortal } from "./handleSubmissionForNchPortal";
import { mergedHpcsCodeWithUniqueJcode } from "./mergedHpcsCodeWithUniqueJcode";

/**
 * Parameters for {@link saveOrderAndNavigate} and related save helpers.
 *
 * @property shouldSaveOnly - When true, persists and clears the dirty flag
 *   without logging continue analytics or navigating away (footer Save flow).
 * @property dumpFormData - Trigger from `useDumpLatestMedicalPaFormData` that
 *   persists the form JSON; resolves to `null` when the request fails.
 */
interface BuildAndSaveOrderDataParams {
  id: string;
  formFieldsData: any;
  latestMedicalPaFormData: LatestMedicalPaFormData;
  generalOrderDetails: any;
  authOnFileDetails: any;
  dumpFormData: (
    payload: DumpLatestMedicalPaFormDataRequest,
  ) => Promise<unknown | null>;
  singleMedicalPaFormConfig: any;
  workflowConfig: any;
  user: User | null;
  orgId: string | null;
  navigate: NavigateFunction;
  faxPdfIntervalRef: MutableRefObject<NodeJS.Timeout | null>;
  setLoading: (value: boolean) => void;
  setFormDirty: (value: boolean) => void;
  hasQuestions?: boolean;
  shouldRunMedicalNeccessity?: boolean;
  shouldSaveOnly?: boolean;
}

/**
 * Prepares the order data by merging form context fields with the existing
 * medical PA order, deduplicating HPCS codes, and attaching the selected
 * form ID for submission.
 *
 * @returns The merged and enriched order data ready to be persisted.
 */
export const buildOrderData = (
  formFieldsData: any,
  singleMedicalPaOrder: any,
  singleMedicalPaFormConfig: any,
) => {
  const data = createOrderDataFromContextFormData(
    formFieldsData,
    singleMedicalPaOrder ?? {},
  );

  data.hpcs_codes = mergedHpcsCodeWithUniqueJcode(
    singleMedicalPaOrder?.hpcs_codes ?? ([] as HpcsCode[]),
    (data?.hpcs_codes ?? []) as HpcsCode[],
  );

  data.selected_form_id_for_submission = singleMedicalPaFormConfig?.id ?? "";

  return data;
};

/**
 * Fires a "Continue Button Clicked" analytics event to BigQuery via the
 * API worker manager. Errors are silently swallowed since analytics
 * failures should never block the user workflow.
 */
export const logContinueButtonEvent = (
  generalOrderDetails: any,
  authOnFileDetails: any,
  singleMedicalPaFormConfig: any,
  user: User | null,
  orgId: string | null,
) => {
  apiWorkerManager
    .enqueueAndProcess({
      patient_id:
        generalOrderDetails?.order?.demographics?.patient_id ??
        generalOrderDetails?.order?.demographics?.patient_mrn ??
        "",
      order_id: generalOrderDetails?.order?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.CONTINUE_BUTTON_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
        source: ScreenNamesForBigQuery.FORM_SCREEN,
        auth_status: authOnFileDetails?.auth_on_file?.auth_status ?? "",
        form_name: singleMedicalPaFormConfig?.data?.formTitle ?? "",
      },
    })
    .catch(() => {});
};

/**
 * Determines post-save navigation based on the workflow's submission type.
 *
 * - For `"fax"` workflows: delegates to {@link handleFaxSubmission} which
 *   either skips medical necessity (directly submitting and polling for the
 *   generated PDF) or navigates to the procedure questionnaire.
 * - For all other workflows: navigates directly to the final document page.
 *
 * @param id - Medical PA order id
 * @param workflowConfig - Workflow config (uses `submission_type`)
 * @param navigate - React Router navigate function
 * @param faxPdfIntervalRef - Ref holding the fax-PDF poll timeout id
 * @param setLoading - Loading flag setter
 * @param hasQuestions - Whether NCH portal path has clinical questions
 * @param shouldRunMedicalNeccessity - Whether NCH path should run MN
 * @param orgId - Tenant org id forwarded to fax submit for the PG path
 */
export const navigateAfterSave = async (
  id: string,
  workflowConfig: any,
  navigate: NavigateFunction,
  faxPdfIntervalRef: MutableRefObject<NodeJS.Timeout | null>,
  setLoading: (value: boolean) => void,
  hasQuestions?: boolean,
  shouldRunMedicalNeccessity?: boolean,
  orgId?: string | null,
) => {
  if (workflowConfig?.submission_type === "fax") {
    await handleFaxSubmission(
      id,
      navigate,
      faxPdfIntervalRef,
      setLoading,
      orgId,
    );
  } else if (workflowConfig?.submission_type === "rpa_nch") {
    await handleSubmissionForNchPortal(
      navigate,
      id,
      hasQuestions ?? false,
      shouldRunMedicalNeccessity ?? false,
      setLoading,
    );
  } else {
    navigate(`/worklists/final-document/${id}`);
  }
};

/**
 * Orchestrates the full save-and-navigate flow when the user clicks
 * "Continue" on the medical PA form:
 *
 * 1. Builds merged order data from form context and existing order
 * 2. Persists the order update to Firestore
 * 3. Clears the form dirty flag
 * 4. Unless `shouldSaveOnly` is true, logs continue analytics and navigates
 *    to the appropriate next screen based on workflow type
 *
 * @param params - See {@link BuildAndSaveOrderDataParams}; `shouldSaveOnly`
 *   defaults to `false`.
 * @returns Resolves when the save (and optional navigation) flow completes.
 */
export const saveOrderAndNavigate = async ({
  id,
  formFieldsData,
  latestMedicalPaFormData,
  generalOrderDetails,
  authOnFileDetails,
  dumpFormData,
  singleMedicalPaFormConfig,
  workflowConfig,
  user,
  orgId,
  navigate,
  faxPdfIntervalRef,
  setLoading,
  setFormDirty,
  hasQuestions,
  shouldRunMedicalNeccessity,
  shouldSaveOnly = false,
}: BuildAndSaveOrderDataParams) => {
  const data = buildOrderData(
    formFieldsData,
    latestMedicalPaFormData,
    singleMedicalPaFormConfig,
  );
  const dumpResult = await dumpFormData({
    order_id: generalOrderDetails?.order?.order_id ?? "",
    org_id: orgId ?? "",
    patient_id:
      generalOrderDetails?.order?.demographics?.patient_id ??
      generalOrderDetails?.order?.demographics?.patient_mrn ??
      "",
    form_data: data,
  });
  // The hook resolves to null on failure; surface it so the caller's
  // try/catch shows the save-failure toast instead of navigating away.
  if (dumpResult === null) {
    throw new Error("Failed to persist latest medical PA form data");
  }
  setFormDirty(false);
  if (shouldSaveOnly) {
    return;
  }
  logContinueButtonEvent(
    generalOrderDetails,
    authOnFileDetails,
    singleMedicalPaFormConfig,
    user,
    orgId,
  );

  await navigateAfterSave(
    id,
    workflowConfig,
    navigate,
    faxPdfIntervalRef,
    setLoading,
    hasQuestions,
    shouldRunMedicalNeccessity,
    orgId,
  );
};
